/* KANON `Raporty` — the selected form's card, transcribed from `01-ankiety.html`:
 *   card     radius 24, --bg-card under a --border hairline, filling the rest of the row
 *   head     22 26 18 padding, a hairline under it, the two controls flush right
 *   eyebrow  10.5 / 700 / .14em, uppercase, --text-3
 *   headline 21 / 800 / -.02em
 *   lede     12.5, --text-2
 *   body     18 26 padding, TWO columns of sections with a 14px gutter
 *   section  radius 24 over --bg-card, its head 14 16 with a 38px tile
 *   question 52px rows divided by a hairline; label 14 / 600, type pill 11 / 600
 *
 * ── The body is a two-column grid and it SCROLLS ─────────────────────────────────────────────
 * The canon's frame is 1440x900 and its own body is `overflow:hidden`, which is a drawing's way of
 * saying "there is more". Fourteen questions do not fit in 900px whatever the column count, so the
 * grid's box is the scrollport — the card keeps its head, the sections scroll under it, and nothing
 * is clipped without a way to reach it (`CLAUDE.md` § Fit-or-scroll).
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

.card {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xl);
}

.head {
  display: flex;
  flex: none;
  gap: var(--space-4);
  align-items: flex-start;

  /* The head HUGS its name and its one line (owner, 4 November 2026); the wait holds the same two
   * lines through `.headlineReserve` and `.ledeReserve`, so the intake lands in the box it held. */
  padding: var(--space-22px) var(--space-26px) var(--space-5);
  border-bottom: var(--border-hairline) solid var(--border);
}

.headText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-6px);
  min-width: 0;
}

.headline {
  margin: var(--space-0);
  overflow: hidden;
  font-size: var(--type-title-1);
  font-weight: var(--fw-semibold);
  line-height: var(--type-title-1-lh);
  letter-spacing: var(--ls-name);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lede {
  margin: var(--space-0);
  font-size: var(--type-control);
  font-weight: var(--fw-regular);
  line-height: var(--type-control-lh);
  color: var(--text-2);
}

/* The wait's two lines, each the height of the line it stands in for. */
.headlineReserve {
  display: flex;
  align-items: center;
  height: var(--type-title-1-lh);
}

.ledeReserve {
  display: flex;
  align-items: center;
  height: var(--type-control-lh);
}

.skelHeadline {
  width: 38%;
  height: 16px;
}

.skelLede {
  width: 72%;
  height: 10px;
}

/* The way into the editor, and the screen's one accent: a 40px ember pill with the pencil and the
 * word „Edytuj" (owner, 4 November 2026 — the bare pencil kept its colour and gained its word). The
 * fill and the focus ring are `shared/ui/emberButton.module.css`'s `.ember.pill`. */
.edit {
  gap: var(--space-2);
  height: var(--sz-report-ibtn-lg);
  padding: var(--space-0) var(--space-18px) var(--space-0) var(--space-14px);
  font-size: var(--type-control);
  letter-spacing: normal;
  text-decoration: none;
  border-radius: var(--r-pill);
}

.editIcon {
  display: block;
  width: var(--sz-report-glyph-md);
  height: var(--sz-report-glyph-md);
}

/* ── The sections ────────────────────────────────────────────────────────────────────────────── */

.body {
  display: grid;
  flex: 1;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-14px);

  /* `max-content`, not the default `auto`, and it is load-bearing rather than tidy. Every section
   * is `overflow: hidden`, which switches OFF a grid item's automatic minimum size — so with `auto`
   * rows the two rows were squeezed to an equal 272.5px inside a 595px box and the taller card's
   * last two questions were clipped with no scroll to reach them. Measured in the running app at
   * 1440x900: `scrollHeight === clientHeight`, which is the clip-without-a-scroll that
   * `CLAUDE.md` § Fit-or-scroll rules out outright. Stating the rows makes the box overflow, and
   * the `overflow-y` below turns that into a scroll. */
  grid-auto-rows: max-content;
  align-content: start;
  min-height: 0;
  padding: var(--space-5) var(--space-26px);
  overflow-y: auto;
}

/* An open section, not a card inside the card (owner, 4 November 2026): its head stands over a
 * hairline and its rows run flush with it. */
.section {
  overflow: hidden;
}

.sectionHead {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  height: var(--h-report-section-head);
  padding: var(--space-0);
}

.sectionText {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.sectionTitle {
  margin: var(--space-0);
  font-size: var(--type-body);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text);
}

.sectionSubtitle {
  font-size: var(--fs-micro);
  font-weight: var(--fw-regular);
  line-height: var(--lh-auto);
  color: var(--text-3);
}

.sectionCount {
  flex: none;
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  font-variant-numeric: tabular-nums;
  line-height: var(--lh-auto);
  color: var(--text-2);
}

.questions {
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
  border-top: var(--border-hairline) solid var(--border);
}

.question {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  min-height: var(--h-report-question);
  padding: var(--space-13px) var(--space-0);
}

.question + .question {
  border-top: var(--border-hairline) solid var(--border);
}

.questionText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-3px);
  min-width: 0;
}

.questionLabel {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text);
}

/* The predefined question's mark, before its word — the same decision and the same ink as the
 * editor's `.rowGlyph` (owner, 17 September 2026). A row here has no hover state, because nothing on
 * this card is pressable: it describes, and the pencil in its head is the one way in. */
.questionGlyph {
  display: block;
  flex: none;
  width: var(--sz-report-glyph-md);
  height: var(--sz-report-glyph-md);
  color: var(--text-3);
}

.questionMeta {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
}

.type {
  font-size: var(--type-label);
  font-weight: var(--fw-regular);
  line-height: var(--lh-auto);
  color: var(--text-3);
}

/* „→ Ciało" — the note that says the answer LEAVES the survey (see `model/surveyDetail`).
 *
 * `--accent-ink`, the same ink the editor's `.mapNoteInk` draws it in: the owner settled the note as
 * ember on 17 September 2026 and confirmed it on 4 November, when this card was still printing it
 * in the primary ink and the two surfaces of one screen disagreed about one mark. `--accent-ink`
 * rather than `--accent-text`, because this is a run of words. */
.mapNote {
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--accent-ink);
}

/* ── The three states with no questions ──────────────────────────────────────────────────────
 * All of them stand INSIDE the card, so its box never changes with its state. */

.state {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-4);
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: var(--space-6);
  text-align: center;
}

/* The wait, in the shape of the sections it is about to be: two columns of a head and three rows. */
.skelSection {
  display: flex;
  flex-direction: column;
}

.skelTitle {
  width: 42%;
  height: 12px;
}

.skelSub {
  width: 28%;
  height: 8px;
}

.skelLabel {
  width: 56%;
  height: 11px;
}

.skelType {
  width: 34%;
  height: 8px;
}
